// Sous-plan 10, lot E2 : la navigation de l'app dans la colonne de Dust (page Assistant). Le modele
// vient du parent (front/lib/compta/navigation_app.ts) ; « Nouvelle conversation » et les pages de
// l'assistant (Agents, Competences) restent dans Dust, les autres entrees renvoient leur intention a
// l'app par message.
// 01/10 (decision de Louka) : trois onglets Travail, Entreprise, Activites
// (front/lib/compta/onglets_navigation.ts) ; la liste des conversations ne s'affiche que sous Travail
// (SidebarMenu lit l'onglet courant).
// Maquette de la coquille (01/10) : avec `coquille`, une entree de l'app s'ouvre dans le rectangle du
// chat (CadreDeLApp) au lieu d'etre renvoyee au parent.

import { BoiteDeReception } from "@app/components/compta/BoiteDeReception";
import { EspacesDeDust } from "@app/components/compta/EspacesDeDust";
import { boiteDuModele } from "@app/lib/compta/boite_de_reception";
import { estActive, routeDeLaPageDeLApp } from "@app/lib/compta/coquille";
import type { EntreeNav } from "@app/lib/compta/navigation_app";
import { envoyer, useNavigationDeLApp } from "@app/lib/compta/navigation_app";
import type {
  EntreeOnglet,
  IdOnglet,
  Pictogramme,
} from "@app/lib/compta/onglets_navigation";
import { construireOnglets } from "@app/lib/compta/onglets_navigation";
import { useAppRouter } from "@app/lib/platform";
import { getConversationRoute } from "@app/lib/utils/router";
import type { WorkspaceType } from "@app/types/user";
import { getWorkspaceDefaultAgentId } from "@app/types/user";
import {
  Announcement01,
  BarChart12,
  Briefcase01,
  Building02,
  Building04,
  Button,
  Calendar,
  CheckCircle,
  Clock,
  ClockRewind,
  CloudArrowLeftRight,
  CreditCard01,
  Cube01,
  cn,
  Edit04,
  File04,
  Folder,
  Globe01,
  GraduationHat01,
  LayersThree01,
  MessageChatCircle,
  MessagePlusCircle,
  NavigationList,
  NavigationListCollapsibleSection,
  NavigationListItem,
  NavigationListLabel,
  PieChart01,
  PuzzlePiece01,
  Robot,
  Route,
  Scales01,
  ShoppingBag01,
  Users01,
  VideoRecorder,
} from "@dust-tt/sparkle";
import type { ComponentType } from "react";

const PICTOGRAMMES: Record<Pictogramme, ComponentType> = {
  agents: Robot,
  competences: PuzzlePiece01,
  connecteurs: CloudArrowLeftRight,
  taches: Clock,
  documents: Folder,
  sites: Globe01,
  espace: Building02,
  video: VideoRecorder,
  diplome: GraduationHat01,
  "compte-rendu": Edit04,
  annonce: Announcement01,
  catalogue: Cube01,
  panier: ShoppingBag01,
  tableau: BarChart12,
  accuse: CheckCircle,
  fournisseurs: Building04,
  clients: Users01,
  calendrier: Calendar,
  paiement: CreditCard01,
  statistiques: PieChart01,
  plan: LayersThree01,
  comparateur: Scales01,
  pointage: ClockRewind,
  parcours: Route,
  page: File04,
};

const ICONES_ONGLETS: Record<IdOnglet, ComponentType> = {
  travail: MessageChatCircle,
  entreprise: Building02,
  activites: Briefcase01,
};

function agir(entree: EntreeNav): void {
  if (entree.reglages) {
    envoyer({ type: "reglages", onglet: entree.reglages });
  } else if (entree.route) {
    envoyer({ type: "naviguer", route: entree.route });
  }
}

function libelleAvecBadge(entree: EntreeNav): string {
  return entree.badge ? `${entree.libelle} (${entree.badge})` : entree.libelle;
}

export function NavigationApp({
  owner,
  onglet,
  setOnglet,
}: {
  owner: WorkspaceType;
  onglet: IdOnglet;
  setOnglet: (onglet: IdOnglet) => void;
}) {
  const nav = useNavigationDeLApp();
  const router = useAppRouter();
  if (!nav) {
    return null;
  }
  // Maquette de la coquille : les pages de l'app s'ouvrent dans le rectangle du chat, et l'entree
  // active est celle de la page affichee (le chat la connait, plus l'app).
  const coquille = nav.coquille === true;
  const brut = coquille ? router.query.chemin : undefined;
  const cheminAffiche =
    coquille && /^\/w\/[^/]+\/app$/.test(router.asPath.split("?")[0])
      ? typeof brut === "string"
        ? brut
        : null
      : null;
  // Taches planifiees (06/10) : la page des taches est une page du chat ; son entree est active
  // quand elle est affichee.
  const cheminDuChat = router.asPath.split("?")[0];
  const marquer = (e: EntreeOnglet): EntreeOnglet =>
    coquille
      ? {
          ...e,
          actif:
            estActive(e.adresse, cheminAffiche) ||
            (e.id === "taches" &&
              e.routeAssistant !== undefined &&
              cheminDuChat === e.routeAssistant),
        }
      : e;
  // Boite de reception (02/10) : annoncee par l'app, elle s'ouvre en petite fenetre sous Travail.
  const boite = boiteDuModele(nav);
  const onglets = construireOnglets(nav.entrees, owner.sId, {
    boiteSurgissante: boite !== null,
    espacesDust: coquille,
    agentPrincipal: coquille ? getWorkspaceDefaultAgentId(owner) : null,
    administrationComplete: nav.administrationComplete === true,
    titreActivites:
      typeof nav.titreActivites === "string" ? nav.titreActivites : undefined,
  });
  const courant = onglets.find((o) => o.id === onglet) ?? onglets[0];
  return (
    <div className="flex shrink-0 flex-col gap-2 px-2 pt-3">
      <div className="flex items-center px-1 pb-3">
        <img
          src={nav.logo}
          alt={nav.compte.organisation || "Logo"}
          className="h-9 w-auto max-w-[170px] object-contain"
        />
      </div>
      <div
        className="grid grid-cols-3 gap-1"
        role="tablist"
        aria-label="Sections"
      >
        {onglets.map((o) => (
          <Button
            key={o.id}
            role="tab"
            aria-selected={o.id === courant.id}
            label={o.libelle}
            icon={ICONES_ONGLETS[o.id]}
            size="sm"
            variant={o.id === courant.id ? "primary" : "ghost"}
            className="w-full justify-center px-1"
            onClick={() => setOnglet(o.id)}
          />
        ))}
      </div>
      {courant.id === "travail" && (
        <Button
          label="Nouvelle conversation"
          icon={MessagePlusCircle}
          variant="highlight"
          size="sm"
          className="mt-3 h-9 self-start px-5 text-base"
          onClick={() => {
            void router.push(getConversationRoute(owner.sId));
          }}
        />
      )}
      {/* Blocs séparés comme chez Dust : un espace et un titre de section entre chaque bloc. */}
      {/* 01/10 soir (Louka) : sous Travail, Agents et Compétences s'écartent du bouton et des
          conversations ; sous Entreprise, les entrées descendent un peu sous les onglets. */}
      <div
        data-bloc-onglet
        className={cn(
          courant.id === "travail" && "mt-3 pb-4",
          courant.id === "entreprise" && "mt-1.5"
        )}
      >
        <NavigationList className="shrink-0 pb-1">
          {courant.sections.map((section, i) => {
            const lignes = section.entrees.map((entree) => (
              <LigneOnglet
                key={entree.id}
                entree={marquer(entree)}
                ouvrir={(e) => {
                  if (e.routeAssistant) {
                    void router.push(e.routeAssistant);
                  } else if (coquille && e.adresse) {
                    void router.push(routeDeLaPageDeLApp(owner.sId, e.adresse));
                  } else {
                    agir(e);
                  }
                }}
              />
            ));
            return (
              <div
                key={section.titre ?? `section-${i}`}
                className={i > 0 ? "mt-4 flex flex-col" : "flex flex-col"}
              >
                {section.titre && (
                  <NavigationListLabel
                    label={section.titre}
                    // Titres de blocs bien distincts des entrées (Utilisateurs, Administration) ;
                    // un cran au-dessus sous Activités (03/10) et pour les sous-catégories (Boosts).
                    className={cn(
                      "font-semibold text-foreground",
                      (courant.id === "activites" || section.sousCategorie) &&
                        "text-base"
                    )}
                  />
                )}
                {section.depliable ? (
                  <NavigationListCollapsibleSection
                    label={section.depliable.libelle}
                    icon={PICTOGRAMMES[section.depliable.pictogramme]}
                    type="collapse"
                    defaultOpen
                  >
                    {lignes}
                  </NavigationListCollapsibleSection>
                ) : (
                  lignes
                )}
              </div>
            );
          })}
          {courant.id === "travail" && boite && (
            <BoiteDeReception owner={owner} boite={boite} />
          )}
        </NavigationList>
        {/* RAG Dust : l'arbre des espaces de Dust, tel quel, sous les entrees de l'app. */}
        {courant.avecEspaces && (
          <div
            // La zone de defilement de l'arbre de Dust prend la hauteur du parent : sans hauteur ici,
            // elle tombait a 0 px. Elle suit son contenu, plafonnee, et defile au-dela.
            className="mt-2 max-h-[60dvh] overflow-y-auto [&_.scrollarea-viewport]:h-auto [&>div:first-child]:h-auto"
          >
            <EspacesDeDust owner={owner} />
          </div>
        )}
      </div>
    </div>
  );
}

function LigneOnglet({
  entree,
  ouvrir,
}: {
  entree: EntreeOnglet;
  ouvrir: (entree: EntreeOnglet) => void;
}) {
  return (
    <NavigationListItem
      label={libelleAvecBadge(entree)}
      icon={entree.pictogramme ? PICTOGRAMMES[entree.pictogramme] : undefined}
      selected={entree.actif}
      status={entree.nonLu ? "unread" : undefined}
      onClick={() => ouvrir(entree)}
    />
  );
}
